import type { SessionUser } from './types';

const STORAGE_KEY = 'gaz.session';

type StoredSession = {
  token: string;
  refresh_token: string;
  token_expires_at: number;
  user: SessionUser | null;
};

export const state: {
  token: string | null;
  refreshToken: string | null;
  expiresAt: number;
  user: SessionUser | null;
} = { token: null, refreshToken: null, expiresAt: 0, user: null };

function jwtExpMs(token: string): number {
  try {
    const part = token.split('.')[1] ?? '';
    const json = JSON.parse(atob(part.replace(/-/g, '+').replace(/_/g, '/'))) as { exp?: number };
    return typeof json.exp === 'number' ? json.exp * 1000 : 0;
  } catch {
    return 0;
  }
}

function emitChanged(): void {
  document.dispatchEvent(new CustomEvent('session:changed'));
}

export function readStoredSession(): StoredSession | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<StoredSession>;
    if (typeof parsed.token !== 'string' || typeof parsed.refresh_token !== 'string') return null;
    return {
      token: parsed.token,
      refresh_token: parsed.refresh_token,
      token_expires_at: Number(parsed.token_expires_at) || jwtExpMs(parsed.token),
      user: parsed.user ?? null,
    };
  } catch {
    return null;
  }
}

function applyStored(s: StoredSession | null): void {
  state.token = s?.token ?? null;
  state.refreshToken = s?.refresh_token ?? null;
  state.expiresAt = s?.token_expires_at ?? 0;
  state.user = s?.user ?? null;
}

export function saveSession(session: {
  token: string;
  refresh_token: string;
  expires_at?: number | null;
  user?: SessionUser | null;
}): void {
  const stored: StoredSession = {
    token: session.token,
    refresh_token: session.refresh_token,
    token_expires_at: session.expires_at || jwtExpMs(session.token),
    user: session.user !== undefined ? session.user : state.user,
  };
  applyStored(stored);
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
  } catch {
    /* ignore */
  }
  emitChanged();
}

export function setUser(user: SessionUser | null): void {
  if (!state.token || !state.refreshToken) return;
  saveSession({ token: state.token, refresh_token: state.refreshToken, expires_at: state.expiresAt, user });
}

export function clearSession(): void {
  applyStored(null);
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
  emitChanged();
}

export function syncSessionFromStorage(): void {
  applyStored(readStoredSession());
}

// Pick up login / logout / refresh done in another tab.
window.addEventListener('storage', (e) => {
  if (e.key !== STORAGE_KEY) return;
  syncSessionFromStorage();
  emitChanged();
});
